// src/client/composables/useModelStore.js
import { ref } from "vue";
import { modelStore, resolveModelByPath } from "~shared/config/models.cfg.js";
import { loadCustomerConfig } from "@/utils/loadCustomerConfig.js";
import { authFetch } from "@/utils/api.js"; // kept for compatibility / future use

import DateRangeFilter from "@/components/ag-grid/DateRangeFilter.vue";
import GroupTypeEditor from "@/components/ag-grid/GroupTypeEditor.vue";

/* ============================================================
   REACTIVE STATE
============================================================ */
const models = modelStore;           // shared definition map
const modelDef = ref(null);          // currently selected model definition
const currentModel = ref(null);      // current model.table (e.g. "article_groups")
const columns = ref(null);           // enhanced column defs for AG Grid

// taxonomy (injected client extras)
const articleGroups = ref([]);
const articleTypes = ref([]);
const PASSWORD_REGEX = /^(?=.*[A-Za-z])(?=.*\d)[^\s]{8,}$/;

/* ============================================================
   SHARED HELPERS
============================================================ */

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return isNaN(d)
    ? value
    : d.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      });
};

// Helper: toggles NZ icon state in AG-grid buttons
function updateIconClass(buttonEl, hasValue) {
  const icon = buttonEl.querySelector("span");
  if (icon) icon.classList.toggle("nz-on", hasValue);
}

/* ============================================================
   UNIVERSAL EDITOR BUTTON FACTORY
============================================================ */

/**
 * Creates a reusable AG-Grid editor button.
 *
 * @param {Object} params - AG-Grid cell renderer params
 * @param {Function} opener - modal opener function
 * @param {Function} callback - callback invoked on successful save
 * @param {Boolean} highlight - optional: enable nz-on highlight state
 */
function createEditorButton({ params, opener, callback, highlight = false }) {
  const btn = document.createElement("button");
  const clss = params.clss || 'pencil';
  btn.classList.add("ag-button");
  btn.style.cssText =
    "padding:2px 6px;cursor:pointer;font-size:18px;border:none;background:transparent;";

  btn.innerHTML = `<span class="icon-${clss}${highlight ? " nz-on" : ""}"></span>`;

  btn.addEventListener("click", (e) => {
    e.stopPropagation?.();

    // Use the injected opener function (type editor, bricks editor, etc.)
    opener(params.data, (result) => {
      if (result === null) return; // Cancel
      callback(result, btn);
    });
  });

  return btn;
}

/* ============================================================
   CELL RENDERERS
============================================================ */

const cellRenderers = {
  gridCheckbox(params) {
    const input = document.createElement("input");
    input.type = "checkbox";
    input.classList.add("ag-checkbox-input");

    input.checked = !!params.value;
    input.style.cursor = "pointer";

    input.addEventListener("change", () => {
      params.node.setDataValue(params.colDef.field, input.checked);
    });

    return input;
  },

  gridCurstockRenderer(params) {
    const container = document.createElement("div");
    container.classList.add("nz-grid-int-wrap");

    const input = document.createElement("input");
    input.type = "text";
    input.classList.add("nz-grid-int-field");
    input.value = params.value ?? "";

    const commit = () => {
      params.node.setDataValue("curstock", input.value);
    };

    input.addEventListener("keydown", (e) => e.key === "Enter" && commit());
    input.addEventListener("blur", commit);

    container.appendChild(input);
    return container;
  },

  /* ------------------------------------------------------------
     TYPE GROUP EDITOR BUTTON
  ------------------------------------------------------------ */
  editTypeGroupRenderer(params) {
    const hasGroup = !!params.data?.hasGroup;

    return createEditorButton({
      params,
      highlight: hasGroup,
      opener: window.$modalOpeners().openTypeGroupEditor,
      callback: (hasGroup, btn) => {
        const newValue = !!hasGroup;
        params.node.setDataValue("hasGroup", newValue);
        updateIconClass(btn, newValue);
      }
    });
  },

  /* ------------------------------------------------------------
     ARTICLE TYPES EDITOR BUTTON
  ------------------------------------------------------------ */
  editTypesRenderer(params) {
    return createEditorButton({
      params,
      opener: window.$modalOpeners().openArticleTypesEditor,
      callback: (hasTypes, btn) => {
        updateIconClass(btn, !!hasTypes);
      }
    });
  },

  /* ------------------------------------------------------------
     ARTICLE MASTER EDITOR BUTTON
  ------------------------------------------------------------ */
  editArticleMasterRenderer(params) {
    return createEditorButton({
      params,
      opener: window.$modalOpeners().openArticleMasterEditor,
      callback: (hasChildren, btn) => {
        updateIconClass(btn, !!hasChildren);
      }
    });
  },

  editArticleDuplicateRenderer(params) {
    
    params = params || {};
    params.clss = "tags";// add a custom icon in the edit cell

    return createEditorButton({
      params,
      opener: window.$modalOpeners().openArticleDuplicateEditor,
      callback: (hasChildren, btn) => {
        updateIconClass(btn, !!hasChildren);
      }
    });
  },

  /* ------------------------------------------------------------
     CURSTOCK TYPE EDITOR BUTTON + TOGGLE
  ------------------------------------------------------------ */
  gridCurstockTypesRenderer(params) {
    const container = document.createElement("div");

    const toggle = document.createElement("input");
    toggle.type = "checkbox";
    toggle.classList.add("ag-checkbox-input");
    toggle.checked = !!params.value;
    toggle.style.cursor = "pointer";

    toggle.addEventListener("change", () => {
      params.node.setDataValue("curstocktypes", toggle.checked);
      params.api.refreshCells({ rowNodes: [params.node], force: true });
    });

    container.appendChild(toggle);

    if (toggle.checked) {
      const btn = createEditorButton({
        params,
        opener: window.$modalOpeners().openStocktypeEditor,
        callback: (hasValues, btn) => {
          updateIconClass(btn, !!hasValues);
        }
      });

      container.appendChild(btn);
    }

    return container;
  },

  /* ------------------------------------------------------------
     BRICKS EDITOR BUTTON - MEDIA / CONTENT
  ------------------------------------------------------------ */
  editBricksRenderer(params) {
    const needsBricks = !!params.data?.needsBricks;
    return createEditorButton({
      params,
      highlight: needsBricks,
      opener: window.$modalOpeners().openBricksEditor,
      callback: (hasBricks, btn) => {
        const newValue = !!needsBricks;
        params.node.setDataValue("needsBricks", newValue);
        updateIconClass(btn, newValue);
      }
    });
  },

  pdfRenderer(params) {
    params = params || {};
    params.clss = "file-pdf";

    return createEditorButton({
      params,

      opener: record => {
        window.$modalOpeners().openPDF(
          record
        );
      },
      callback: () => {}

    });

  },

  removeRenderer(params) {
    params = params || {};
    params.clss = "trash";

    return createEditorButton({
      params,

      opener: record => {
        window.$modalOpeners().openRemoveConfirm(
          record,
          confirmedRecord => {
            // 🔁 delegate upward
            params.context?.onRemove?.(confirmedRecord);
          }
        );
      },

      callback: (hasChildren, btn) => {
        updateIconClass(btn, !!hasChildren);
      }
    });
  }




};

/* ============================================================
   APPLY CLIENT ENHANCEMENTS
    - Works on a *copy* of column defs, not the originals
    - Adds special renderers / editors
    - Removes unwanted cols as defined in the customer config

============================================================ */
async function applyClientEnhancements(identifier, cols) {

  const cfg = await loadCustomerConfig();
  const groups = articleGroups.value;
  const types = articleTypes.value;
  const jsonldMap = {
    product: 'Product',
    blog: 'Blog',
    author: 'Writer',
    artist: 'Artist',
    team: 'Team',
    supplier: 'Supplier',
    fb: 'Facebook',
    creator: 'Creator'
  };

  if( identifier && cfg?.[identifier]?.omit?.length ){
    const omitSet = new Set(cfg[identifier].omit);
    cols = cols.filter(col => !omitSet.has(col.field));

  }

  cols.forEach((el) => {

    if (el.cellRenderer && cellRenderers[el.cellRenderer]) {
      if (el.cellRenderer === "gridCurstockRenderer") {
        el.editable = (p) => !p?.data?.curstocktypes;
        el.cellStyle = (p) =>
          p?.data?.curstocktypes
            ? { opacity: 0.5, "pointer-events": "none" }
            : null;
      }

      el.cellRenderer = cellRenderers[el.cellRenderer];
    }

    if (el.cellEditor ) {

      switch (el.field) {

        case "jsonld":
          el.cellEditor = 'agSelectCellEditor';
          el.cellEditorParams = {
            values: Object.keys(jsonldMap)
          };
          el.valueFormatter = params =>
            jsonldMap[params.value] ?? params.value;

          break;

        case "password":
          el.cellEditor = PasswordCellEditor;
          el.cellRenderer = () => '••••••••';
          el.editable = true;
          el.valueSetter = params => {
                    if (!params.newValue) return false;
                    params.data._plainPassword = params.newValue;
                    return true;
                };
          break;

        default:
          break;

      }

    }

    if (el.type === "date") {
      el.filter = "agDateColumnFilter";
      el.valueFormatter = (p) => formatDate(p.value);
      el.suppressMenu = true;
      el.filterable = true;
      el.buttons = ["reset"];

      el.filterParams = {
        suppressAndOrCondition: false,
        defaultJoinOperator: "OR",
        suppressConditionAndCondition: true,
        filterOptions: ["inRange"],
        defaultOption: "inRange",
        buttons: ["reset"],
        comparator: () => 0
      };

      el.floatingFilterComponent = DateRangeFilter;
      el.floatingFilterComponentParams = { suppressFilterButton: true };
    }

    if (el.field === "type_id") {
      el.headerName = "Type";
      el.editable = true;
      el.cellEditor = GroupTypeEditor;
      el.cellEditorParams = { groups, types };

      el.valueFormatter = (p) => {
        const t = types.find((t) => t.type_id === p.value);
        return t ? t.type_name : "";
      };
    }
  });

  return cols;
}

/* ============================================================
   INTERNAL: RESOLVE MODEL BY PATH OR KEY
============================================================ */
function resolveModelIdentifier(identifier) {
  if (!identifier || typeof identifier !== "string") return null;

  // Preferred: path (e.g. "article-groups")
  let resolved = resolveModelByPath(identifier);

  // Fallback: direct key (e.g. "article_groups")
  if (!resolved && modelStore[identifier]) {
    resolved = { key: identifier, def: modelStore[identifier] };
  }

  return resolved;
}

class PasswordCellEditor {
  init(params) {
    this.valid = false;

    this.eContainer = document.createElement('div');
    this.eContainer.className = 'nz-pass-editor';

    this.eInput = document.createElement('input');
    this.eInput.type = 'text';
    this.eInput.className =
      'ag-input-field-input ag-text-field-input nz-pass-input';
    this.eInput.placeholder = 'New password';

    this.eHint = document.createElement('div');
    this.eHint.className = 'nz-pass-hint';
    this.eHint.textContent = 'Min 8 chars, letters + numbers';

    this.eContainer.appendChild(this.eInput);
    this.eContainer.appendChild(this.eHint);

    this.eInput.addEventListener('input', () => {
      const val = this.eInput.value;
      this.valid = PASSWORD_REGEX.test(val);

      this.eInput.classList.toggle('invalid', !this.valid);
      this.eContainer.classList.toggle('nz-invalid', !this.valid);

      this.eHint.style.visibility = this.valid ? 'hidden' : 'visible';
    });

  }

  getGui() {
    return this.eContainer;
  }

  afterGuiAttached() {
    this.eInput.focus();
  }

  getValue() {
    return this.valid ? this.eInput.value : null;
  }

  isCancelAfterEnd() {
    return !this.valid;
  }
}


/* ============================================================
   MAIN STORE API
============================================================ */
export function useModelStore() {
  async function setModel(identifier) {
    console.log("🔄 setModel called with:", identifier);

    const resolved = resolveModelIdentifier(identifier);

    if (!resolved) {
      console.error("❌ Unknown model identifier:", identifier);
      return;
    }

    const def = resolved.def;

    modelDef.value = def;
    currentModel.value = def.table;

    // Shallow clone columns so we never mutate shared modelStore definitions
    const clonedCols = (def.columns || []).map((c) => ({ ...c }));
    columns.value = await applyClientEnhancements(identifier, clonedCols);
  }

  return {
    models,
    modelDef,
    columns,
    currentModel,
    setModel,
    articleGroups,
    articleTypes
  };
}
